import { motion } from "framer-motion";
import { Info, ChevronRight, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Bandeau d'introduction affiché en haut de chaque page stagiaire.
 * Toujours visible → indique clairement à quoi sert la page.
 * Peut afficher un badge "X action(s) à faire" pour créer un signal fort.
 */
export default function PageIntro({
    eyebrow,
    title,
    description,
    tips = [],
    actionCount = 0,
    actionSectionKey,
}) {
    return (
        <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="page-intro"
            className="mb-10"
        >
            <div className="text-xs uppercase tracking-[0.25em] text-brand-orange">
                {eyebrow}
            </div>
            <h1 className="mt-3 font-display text-4xl font-normal leading-[1.02] tracking-tightest sm:text-5xl">
                {title}
            </h1>
            {description && (
                <p className="mt-3 max-w-2xl text-base text-brand-smoke">{description}</p>
            )}

            {(tips.length > 0 || actionCount > 0) && (
                <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-brand-orange/25 bg-brand-orange/[0.05] p-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                        {actionCount > 0 ? (
                            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-orange" />
                        ) : (
                            <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-orange" />
                        )}
                        <div>
                            {actionCount > 0 && (
                                <div className="mb-1 text-sm font-semibold text-brand-ink" data-testid="page-intro-action-count">
                                    Vous avez {actionCount} action{actionCount > 1 ? "s" : ""} à faire sur cette page.
                                </div>
                            )}
                            {tips.length > 0 && (
                                <ul className="space-y-1 text-sm text-brand-ink/80">
                                    {tips.map((tip, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-brand-orange" />
                                            <span>{tip}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    {actionCount > 0 && actionSectionKey && (
                        <Link
                            to="/stagiaire"
                            data-testid="page-intro-actions-cta"
                            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-brand-orange px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#d9772c]"
                        >
                            Voir mes actions <ChevronRight className="h-3.5 w-3.5" />
                        </Link>
                    )}
                </div>
            )}
        </motion.section>
    );
}
